import { Link } from 'react-router-dom';
import {
  AlertTriangle, CalendarClock, CheckCircle2, FileText, LayoutDashboard,
} from 'lucide-react';
import { useDashboard, useCurrentUser } from '../../lib/api/queries';
import { ATTENTION_REASON_LABELS } from '../../lib/api/types';
import type { DashboardItem, DashboardStageCount, Progress } from '../../lib/api/types';

/**
 * Cross-module view of what is still open. Three bands:
 *
 *   1. Pipeline  — how many items sit at each stage
 *   2. Open work — the items themselves, soonest-due first
 *   3. Attention — the subset carrying a flag
 *
 * The attention band is derived from the same `items` array as band 2 rather
 * than fetched separately, so the two can never disagree about what is open.
 */
export function DashboardPage() {
  const { data: user } = useCurrentUser();
  const { data, isLoading, error } = useDashboard(!!user);

  const items = data?.items ?? [];
  const needsAttention = items.filter(i => i.attention.length > 0);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-accent-alt-700">Dashboard</h1>
        <p className="text-sm text-gray-500">
          Open work across every product your tenant is signed up to.
        </p>
      </header>

      {isLoading && <p className="text-gray-500">Loading…</p>}
      {error && <p className="text-danger-500">Couldn't load the dashboard — is the API running?</p>}

      {data && (
        <>
          <Pipeline stages={data.pipeline} />

          <section className="space-y-3">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Needs attention
            </h2>
            {needsAttention.length === 0 ? (
              <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-600">
                <CheckCircle2 className="w-4 h-4 text-success-500" aria-hidden />
                Nothing needs attention.
              </div>
            ) : (
              <ul className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
                {needsAttention.map(i => <WorkItemRow key={i.id} item={i} showAttention />)}
              </ul>
            )}
          </section>

          <section className="space-y-3">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Open work ({items.length})
            </h2>
            {items.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border border-dashed border-gray-300">
                <LayoutDashboard className="w-10 h-10 text-gray-400 mx-auto" aria-hidden />
                <p className="mt-3 text-gray-600">Nothing open right now.</p>
              </div>
            ) : (
              <ul className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
                {items.map(i => <WorkItemRow key={i.id} item={i} />)}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}

/**
 * Counts per stage, grouped by product. Grouping only shows a product heading
 * when the tenant has more than one — a single-product tenant doesn't need to
 * be told which product it is.
 */
function Pipeline({ stages }: { stages: DashboardStageCount[] }) {
  const products = [...new Set(stages.map(s => s.product))];
  if (stages.length === 0) return null;

  return (
    <div className="space-y-4">
      {products.map(product => (
        <section key={product} className="space-y-2">
          {products.length > 1 && (
            <h2 className="text-sm font-semibold text-gray-700">{product}</h2>
          )}
          <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
            {stages
              .filter(s => s.product === product)
              .map(s => (
                <div
                  key={`${s.product}-${s.stage}`}
                  className="rounded-lg border border-gray-200 bg-white px-4 py-3"
                >
                  <p className="text-2xl font-semibold text-accent-alt-700">{s.count}</p>
                  <p className="text-xs text-gray-500">{s.stage}</p>
                </div>
              ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function WorkItemRow({ item, showAttention = false }: { item: DashboardItem; showAttention?: boolean }) {
  return (
    <li>
      <Link
        to={item.detailPath}
        className="flex items-center gap-4 px-4 py-3 hover:bg-accent-alt-50 transition-colors"
      >
        <FileText className="w-5 h-5 text-accent-alt-500 shrink-0" aria-hidden />

        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 truncate">{item.title}</p>
          <p className="text-xs text-gray-500 truncate">
            {item.organisationName ?? 'No client set'} · {item.stage}
          </p>
          {showAttention && (
            <p className="mt-1 flex flex-wrap gap-1">
              {item.attention.map(r => (
                <span
                  key={r}
                  className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full
                             bg-warning-500/15 text-warning-500"
                >
                  <AlertTriangle className="w-3 h-3" aria-hidden />
                  {ATTENTION_REASON_LABELS[r]}
                </span>
              ))}
            </p>
          )}
        </div>

        <div className="hidden sm:flex flex-col items-end gap-1 shrink-0">
          <ProgressPair form={item.formProgress} report={item.reportProgress} />
          {item.dueDate && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-500">
              <CalendarClock className="w-3 h-3" aria-hidden />
              due {new Date(item.dueDate).toLocaleDateString()}
            </span>
          )}
        </div>
      </Link>
    </li>
  );
}

/**
 * Two counts side by side, never blended into one percentage. An audit at
 * "168 of 168" answered with "0 of 9" report sections written is not 95% done,
 * and any weighting that says otherwise is a number nobody can reproduce.
 *
 * The form total is an upper bound — conditional-hide rules mean some items
 * aren't applicable to a given audit — which is the other reason these are
 * counts rather than percentages. The exact figure is on the audit's scorecard.
 */
function ProgressPair({ form, report }: { form: Progress | null; report: Progress | null }) {
  if (!form && !report) return null;

  return (
    <span className="text-xs text-gray-600 tabular-nums" title="Items answered · report sections drafted">
      {form && <>Form {form.done}/{form.total}</>}
      {form && report && <span className="text-gray-300"> · </span>}
      {report && <>Report {report.done}/{report.total}</>}
    </span>
  );
}
